import { 
  Box, 
  IconButton, 
  Typography, 
  Tooltip, 
  Chip,
  Card,
  CardContent,
  Stack,
  useTheme
} from "@mui/material";
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import { BlindTestStatus, CountSessions, GroupedSessions, IBlindTest } from "../../../Models/IBlindTest";
import { IUnitConfiguration } from "../../../Models/IUnitConfiguration";
import { getFormattedDate } from "../../../Utils/UnitsHelper";
import { IMsalContext, useMsal } from "@azure/msal-react";
import { useEffect, useMemo, useState } from "react";
import { ApiService } from "../../../Services/ApiService";
import { useNavigate } from "react-router";
import { IBlindTestState } from "../../../Models/IBlindTestState";

/**
 * Blind test list component for displaying the list of blind tests 
 * Material UI version
 *
 * @param {*} props
 * @return {*} 
 */
export const BlindTestList: React.FC<{ blindTests: IBlindTest[], unitConfiguration: IUnitConfiguration }> = (props) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const ctx: IMsalContext = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);

    const [blindTests, setBlindTests] = useState<IBlindTest[]>();
    const [countGroupedSession, setCountGroupedSession] = useState<CountSessions[]>();

    // Styles following Material UI patterns
    const styles = {
        container: {
            padding: theme.spacing(2),
        },
        listTitle: {
            marginLeft: theme.spacing(2),
            marginBottom: theme.spacing(2),
            fontWeight: 'bold',
        },
        blindTestCard: {
            marginBottom: theme.spacing(2),
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4],
            },
        },
        cardContent: {
            padding: theme.spacing(2),
            '&:last-child': {
                paddingBottom: theme.spacing(2),
            },
        },
        actionButtons: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginRight: theme.spacing(2),
        },
        publishedBadge: {
            position: 'absolute',
            top: theme.spacing(1),
            right: theme.spacing(1),
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '& .MuiChip-icon': {
                color: 'white',
            },
        },
        infoGrid: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(3),
            alignItems: 'center',
            flex: 1,
        },
        infoItem: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(0.5),
        },
        infoLabel: {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: theme.palette.text.secondary,
        },
        infoValue: {
            fontSize: '0.875rem',
            color: theme.palette.text.primary,
        },
        statusChips: {
            display: 'flex',
            gap: theme.spacing(1),
            alignItems: 'center',
        },
        cardContainer: {
            position: 'relative',
        },
        mainContent: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
        }
    };

    useEffect(() => {
        setBlindTests(props.blindTests);
        let countSessions = new Array<CountSessions>();

        // Count the number of sessions that are unassigned and finished for each blind test 
        for (let i = 0; i < props.blindTests.length; i++) {
            const unassignedSessions = props.blindTests[i].positionDesignSessions.filter(session => session.source === 'Unassigned');
            const finishedSessions = props.blindTests[i].positionDesignSessions.filter(session => session.source != 'Unassigned');

            const unAssignedGroupedSessions: GroupedSessions = unassignedSessions.reduce((acc: GroupedSessions, session) => {
                const { sectionId } = session;
                acc[sectionId!] = (acc[sectionId!] || 0) + 1;
                return acc;
            }, {});
            
            const successGroupedSessions: GroupedSessions = finishedSessions.reduce((acc: GroupedSessions, session) => {
                const { sectionId } = session;
                acc[sectionId!] = (acc[sectionId!] || 0) + 1;
                return acc;
            }, {});

            const unAssignedgroupedSessionsLength = Object.keys(unAssignedGroupedSessions).length;
            const finishedGroupedSessionsLength = Object.keys(successGroupedSessions).length;
            countSessions.push({ 
                ToDo: unAssignedgroupedSessionsLength, 
                Done: finishedGroupedSessionsLength, 
                BlindTestDataSetId: props.blindTests[i].blindTestSetId 
            });
        }
        setCountGroupedSession(countSessions);
    }, [props.blindTests]);

    /**
     * Publish blind test to SME 
     *
     * @param {IBlindTest} blindTest
     * @param {React.MouseEvent} event
     */
    const publishBlindtest = async (blindTest: IBlindTest, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent card click
        blindTest.status = BlindTestStatus.Published;
        const response = await apiService.postAsync(blindTest, `blindtests/publish`);
        if (!response.ok) {
            throw new Error('Could not mark blind test for SME');
        }
        window.location.reload();
    };

    /**
     * Delete blind test 
     *
     * @param {IBlindTest} blindTest
     * @param {React.MouseEvent} event
     */
    const deleteBlindTest = async (blindTest: IBlindTest, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent card click
        const response = await apiService.delete(`blindtests/${blindTest.id}`);
        if (!response.ok) {
            throw new Error('Could not delete blind test');
        }
        window.location.reload();
    };

    /**
     * Navigate to blind test details
     *
     * @param {IBlindTest} blindTest
     */
    function goToDetails(blindTest: IBlindTest) {
        let stateParam: IBlindTestState = { 
            blindTest: blindTest, 
            unitConfiguration: props.unitConfiguration 
        };
        navigate("/blindtests/overview", { state: stateParam });
    }

    return (
        <Box sx={styles.container}>
            {blindTests && blindTests.length > 0 && (
                <Box>
                    <Typography variant="h6" sx={styles.listTitle}>
                        Blind tests ({blindTests?.length})
                    </Typography>
                    
                    <Stack spacing={2}>
                        {blindTests?.map((blindTest, index) => {
                            const sessionCount = countGroupedSession?.find(x => x.BlindTestDataSetId === blindTest.blindTestSetId);
                            
                            return (
                                <Card 
                                    key={`blindtest-${index}`} 
                                    sx={styles.blindTestCard}
                                    onClick={() => goToDetails(blindTest)}
                                >
                                    <Box sx={styles.cardContainer}>
                                        {blindTest.status === BlindTestStatus.Published && (
                                            <Chip
                                                icon={<LockIcon />}
                                                label="Published - 🚀"
                                                sx={styles.publishedBadge}
                                                size="small"
                                            />
                                        )}
                                        
                                        <CardContent sx={styles.cardContent}>
                                            <Box sx={styles.mainContent}>
                                                <Box sx={styles.actionButtons}>
                                                    <Tooltip title="Release to SME">
                                                        <span>
                                                            <IconButton
                                                                disabled={blindTest.status === BlindTestStatus.Published}
                                                                onClick={(e) => publishBlindtest(blindTest, e)}
                                                                color="primary"
                                                                size="small"
                                                            >
                                                                <RocketLaunchIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    
                                                    <Tooltip title={
                                                        blindTest.status === BlindTestStatus.New 
                                                            ? 'Delete' 
                                                            : 'Published blind tests cannot be deleted.'
                                                    }>
                                                        <span>
                                                            <IconButton
                                                                disabled={blindTest.status === BlindTestStatus.Published}
                                                                onClick={(e) => blindTest.status === BlindTestStatus.New ? deleteBlindTest(blindTest, e) : undefined}
                                                                sx={{ 
                                                                    color: blindTest.status === BlindTestStatus.New 
                                                                        ? theme.palette.error.main 
                                                                        : theme.palette.action.disabled 
                                                                }}
                                                                size="small"
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Box>

                                                <Box sx={styles.infoGrid}>
                                                    <Box sx={styles.infoItem}>
                                                        <Typography variant="caption" sx={styles.infoLabel}>
                                                            Blindtest id
                                                        </Typography>
                                                        <Typography variant="body2" sx={styles.infoValue}>
                                                            {blindTest.blindTestSetId}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={styles.infoItem}>
                                                        <Typography variant="caption" sx={styles.infoLabel}>
                                                            Uploaded by
                                                        </Typography>
                                                        <Typography variant="body2" sx={styles.infoValue}>
                                                            {blindTest.user}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={styles.infoItem}>
                                                        <Typography variant="caption" sx={styles.infoLabel}>
                                                            Created
                                                        </Typography>
                                                        <Typography variant="body2" sx={styles.infoValue}>
                                                            {getFormattedDate(blindTest.createdDate.toString())}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={styles.infoItem}>
                                                        <Typography variant="caption" sx={styles.infoLabel}>
                                                            Modified
                                                        </Typography>
                                                        <Typography variant="body2" sx={styles.infoValue}>
                                                            {blindTest.modified.toString() !== '0001-01-01T00:00:00' 
                                                                ? getFormattedDate(blindTest.modified.toString()) 
                                                                : 'Never'
                                                            }
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={styles.infoItem}>
                                                        <Typography variant="caption" sx={styles.infoLabel}>
                                                            Status
                                                        </Typography>
                                                        <Box sx={styles.statusChips}>
                                                            <Chip
                                                                label={`${sessionCount?.ToDo || 0} To do`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                            <Chip
                                                                label={`${sessionCount?.Done || 0} Done`}
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Box>
                                </Card>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}; 